import { findNearMatches, normaliseName } from "./matching.js";
/**
 * Whether two vintage answers describe the same bottling.
 *
 * Three states, not two: a year, no year at all, and *non-vintage*, which is a
 * claim rather than an absence. An NV Champagne and the 2012 of the same cuvée
 * are different wines and the flag is what says so — so a candidate carrying
 * the flag matches only entries carrying it, whatever year either one has.
 *
 * A null year on both sides counts as agreement. It means *the year is not
 * recorded* on two entries with the same producer and the same name, which in
 * a catalogue is one wine written twice far more often than it is two.
 */
function sameBottling(candidate, wine) {
    if (candidate.isNonVintage || wine.isNonVintage) {
        return candidate.isNonVintage && wine.isNonVintage;
    }
    return candidate.vintage === wine.vintage;
}
/**
 * Catalogue entries this registration may be a second copy of, the duplicates
 * first and then by number.
 *
 * **How far the fuzziness goes, and why it stops there.** The producer is
 * matched the way the picker matches it — the same `findNearMatches`, so
 * `Poderi Luigi Einaudi` finds `Luigi Einaudi` — because a producer written
 * two ways is precisely how a second catalogue entry comes to exist. The name
 * is matched the same way, and it has to be: the wine's name has no picker
 * behind it, so `La Grande Annee` typed without the accent is caught here or
 * nowhere.
 *
 * It stops at the vintage, which is compared exactly. A year is four digits
 * read off a label, a mistyped one is a different wine rather than a different
 * spelling, and a rule that treated 2012 and 2013 as near neighbours would
 * offer to put the wrong bottles behind the wrong number.
 */
export function findDuplicateWines(candidate, catalogue) {
    const producer = normaliseName(candidate.producer);
    const name = normaliseName(candidate.name);
    if (producer === "" || name === "")
        return [];
    // One pass over the distinct producers rather than a comparison per wine:
    // a cellar holds far more wines than houses, and the answer is the same for
    // every wine of one house.
    const producers = new Set([
        producer,
        ...findNearMatches(candidate.producer, catalogue.map((wine) => wine.producer)).map((match) => normaliseName(match.value)),
    ]);
    const fromTheSameProducer = catalogue.filter((wine) => producers.has(normaliseName(wine.producer)));
    const names = new Set([
        name,
        ...findNearMatches(candidate.name, fromTheSameProducer.map((wine) => wine.name)).map((match) => normaliseName(match.value)),
    ]);
    return fromTheSameProducer
        .filter((wine) => names.has(normaliseName(wine.name)))
        .map((wine) => ({
        wine,
        kind: sameBottling(candidate, wine)
            ? "same-vintage"
            : "other-vintage",
    }))
        .sort((a, b) => Number(b.kind === "same-vintage") - Number(a.kind === "same-vintage") ||
        a.wine.number - b.wine.number);
}
//# sourceMappingURL=duplicate-wine.js.map